import {
  useGetCurrentUserQuery,
  useGetDemoQuery,
  useGetDemoSearchLinksResultsQuery,
  usePostEventMutation,
} from '@carboncopyco/data';
import {
  ArchiveIcon,
  BalloonOutlineIcon,
  CopyOutlineIcon,
  DuplicateOutlineIcon,
  EditIcon,
  LinkIcon,
  OpenInNew1OutlineIcon,
  ShowOutlineIcon,
  SubdirectoryRightOutlineIcon,
} from '@carboncopyco/icons';
import {
  EventAction,
  EventTarget,
  PermissionAction,
  PermissionKey,
  RecordingTypes,
} from '@carboncopyco/types';
import {
  Dropdown,
  DropdownItem,
  Separator,
  TextCaption,
  Tooltip,
} from '@carboncopyco/ui';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import NiceModal from '@ebay/nice-modal-react';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useRef, useState } from 'react';
import { useIsDemoLocked } from '../../../hooks';
import { useCopyUrl } from '../../../hooks/useCopyUrl';
import { AssetLockedModal } from '../../AssetLockedModal';
import { useDesktopDeeplink, usePermissionsContext } from '../../utils';
import { useLinksPermissions } from '../../utils/hooks/useLinksPermissions';
import { DemoActionsDropdownContent } from './DemoActionsDropdownContent';
import {
  ArchiveDemoModal,
  CopyDemoModalWithFolders,
  DuplicateDemoModal,
  MoveDemoModal,
} from './modals';
import type { DemoActionsDropdownProps } from './types';

const location = 'demo_info_page_actions_menu';

export const DemoPageActionsDropdown = ({
  triggerElement,
  arrow,
  side,
  align,
  asChild,
  goToEditor,
  goToDemoPreview,
  goToCopiedDemo,
  handleCreateTour,
  goToDuplicatedDemo,
  organizationId,
  demoId,
  workspaceId,
  goToWorkspacePage,
  goToFolderPage,
}: DemoActionsDropdownProps) => {
  const { trackEvent } = useAnalytics();
  const [postEvent] = usePostEventMutation();
  const { getPermissions } = usePermissionsContext();
  const archiveRef = useRef<HTMLDivElement>(null);
  const { getIsDemoShareable } = useLinksPermissions();
  const { data: demo } = useGetDemoQuery({
    organizationId,
    workspaceId,
    id: demoId,
  });
  const flags = useFlags();
  const { copyCurrentURL } = useCopyUrl();
  const { data: linksResponse } = useGetDemoSearchLinksResultsQuery({
    organizationId,
    id: demoId,
    active: true,
    workspaces: [workspaceId],
  });
  const duplicateDemoPermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.duplicateDemo
  );
  const createTourPermission = getPermissions(
    PermissionKey.tour,
    PermissionAction.createTour
  );
  const copyToWorkspacePermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.copyDemo
  );
  const editDemoPermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.updateDemoById
  );
  const deleteDemoPermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.deleteDemoById
  );
  const moveDemoPermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.moveDemo
  );

  const [
    showDuplicateDemoModal,
    {
      setTrue: setTrueShowDuplicateDemoModal,
      setFalse: setFalseShowDuplicateDemoModal,
    },
  ] = useBoolean(false);
  const [
    showArchiveDemoModal,
    {
      setTrue: setTrueShowArchiveDemoModal,
      setFalse: setFalseShowArchiveDemoModal,
    },
  ] = useBoolean(false);
  const { exeDeeplinkWithAction, shouldShowDeeplink } = useDesktopDeeplink();

  const [isDemoLockedModal, setIsDemoLockedModal] = useState(false);
  const { data: userData } = useGetCurrentUserQuery();

  const { checkIsDemoLocked, userName } = useIsDemoLocked({
    demoId,
    organizationId,
    currentUserEmail: userData.user.email,
    shouldRunInterval: false,
  });

  const isDemoShareable = getIsDemoShareable(demo);
  const hasAnyAction =
    createTourPermission ||
    editDemoPermission ||
    duplicateDemoPermission ||
    copyToWorkspacePermission ||
    isDemoShareable ||
    deleteDemoPermission;

  const demoFolderId = String(demo?.folderId);

  if (!hasAnyAction) {
    return null;
  }
  const handleEditDemo = () => {
    exeDeeplinkWithAction(
      {
        action: 'edit',
        assetId: demoId,
        organizationId: organizationId,
        workspaceId: workspaceId,
        assetType: 'demo',
      },
      goToEditor
    );
  };
  const isEditMobileDisabled =
    demo.recordingType === RecordingTypes.mobile && !flags.editMobileCloneDemo;
  const allowDemoEdit =
    !demo.readOnly && editDemoPermission && !isEditMobileDisabled;
  const allowTourCreate =
    !demo.readOnly && createTourPermission && !isEditMobileDisabled;

  return (
    <>
      <Dropdown
        width={200}
        asChild={asChild}
        triggerElement={triggerElement}
        {...{ arrow, side, align }}
      >
        <DemoActionsDropdownContent
          createTourFromDemo={
            allowTourCreate && (
              <DropdownItem
                iconLeft={<BalloonOutlineIcon size={18} />}
                label="Create tour"
                onClick={(event) => {
                  event.preventDefault();
                  trackEvent(AnalyticsTrackEventName.DEMO_CLICK_CREATE_TOUR, {
                    location,
                  });
                  handleCreateTour();
                }}
              />
            )
          }
          editDemo={
            allowDemoEdit && (
              <DropdownItem
                iconLeft={<EditIcon size={18} />}
                label="Edit"
                iconRight={
                  shouldShowDeeplink ? (
                    <OpenInNew1OutlineIcon size={15} />
                  ) : null
                }
                onClick={async (event) => {
                  trackEvent(AnalyticsTrackEventName.DEMO_CLICK_EDIT, {
                    location,
                  });

                  if (flags.editorMultiUserLocking) {
                    const lockedBy = await checkIsDemoLocked();
                    if (lockedBy) {
                      setIsDemoLockedModal(true);

                      trackEvent(AnalyticsTrackEventName.DEMO_MULTI_USER_LOCK, {
                        location,
                        variables: {
                          userEditing: lockedBy,
                          userTryingToEdit: userData.user.email,
                        },
                        demoId,
                      });
                      return;
                    }
                  }

                  event.preventDefault();
                  handleEditDemo();
                }}
              />
            )
          }
          duplicateDemo={
            !demo.readOnly &&
            duplicateDemoPermission && (
              <DropdownItem
                iconLeft={<DuplicateOutlineIcon size={18} />}
                label="Duplicate"
                onClick={() => {
                  trackEvent(AnalyticsTrackEventName.DEMO_CLICK_DUPLICATE, {
                    location,
                  });
                  setTrueShowDuplicateDemoModal();
                }}
              />
            )
          }
          moveDemo={
            !demo.readOnly &&
            moveDemoPermission && (
              <DropdownItem
                iconLeft={<SubdirectoryRightOutlineIcon size={18} />}
                label="Move to..."
                onClick={() => {
                  NiceModal.show(MoveDemoModal, {
                    demo,
                    organizationId,
                    workspaceId,
                  });
                }}
              />
            )
          }
          copyDemo={
            !demo.readOnly &&
            copyToWorkspacePermission && (
              <DropdownItem
                iconLeft={<CopyOutlineIcon size={18} />}
                label="Copy to..."
                onClick={() => {
                  trackEvent(
                    AnalyticsTrackEventName.DEMO_CLICK_COPY_TO_WORKSPACE,
                    {
                      location,
                    }
                  );
                  NiceModal.show(CopyDemoModalWithFolders, {
                    goToCopiedDemo,
                    organizationId,
                    demo,
                  });
                }}
              />
            )
          }
          copyURL={
            !demo.readOnly &&
            flags.copyUrl && (
              <DropdownItem
                iconLeft={<LinkIcon size={18} />}
                label="Copy page URL"
                onClick={() => {
                  copyCurrentURL();
                }}
              />
            )
          }
          archiveDemo={
            !demo.readOnly &&
            deleteDemoPermission && (
              <>
                <Separator horizontal />
                <div ref={archiveRef}>
                  <DropdownItem
                    disabled={!!linksResponse?.metadata?.totalItems}
                    iconLeft={<ArchiveIcon size={18} />}
                    label="Archive"
                    onClick={() => {
                      trackEvent(AnalyticsTrackEventName.DEMO_CLICK_ARCHIVE, {
                        location,
                      });
                      setTrueShowArchiveDemoModal();
                    }}
                  />
                </div>
                {!!linksResponse?.metadata?.totalItems && (
                  <Tooltip
                    anchorRef={archiveRef}
                    placement="bottom"
                    offset={[0, 24]}
                  >
                    <div className="p-2.5">
                      <TextCaption
                        label="To archive this demo, please deactivate all of its shared links first"
                        color="text-ds-natural-600"
                      />
                    </div>
                  </Tooltip>
                )}
              </>
            )
          }
          previewDemo={
            <DropdownItem
              iconLeft={<ShowOutlineIcon size={18} />}
              label="Preview"
              onClick={() => {
                postEvent({
                  organizationId,
                  workspaceId,
                  demoId: String(demo.id),
                  action: EventAction.PREVIEWED,
                  target: EventTarget.DEMO,
                });
                goToDemoPreview();
              }}
            />
          }
        />
      </Dropdown>
      {isDemoLockedModal && (
        <AssetLockedModal
          userName={userName}
          buttonLabel="Okay"
          onButtonClick={() => {
            setIsDemoLockedModal(false);
          }}
        />
      )}
      {showDuplicateDemoModal && (
        <DuplicateDemoModal
          organizationId={organizationId}
          demoId={demoId}
          onClose={setFalseShowDuplicateDemoModal}
          show={showDuplicateDemoModal}
          goToDuplicatedDemo={goToDuplicatedDemo}
          folderId={demoFolderId}
        />
      )}
      {showArchiveDemoModal && (
        <ArchiveDemoModal
          folderId={demoFolderId}
          organizationId={organizationId}
          demoId={demoId}
          show={showArchiveDemoModal}
          onClose={setFalseShowArchiveDemoModal}
          navigateOnSuccess={() => {
            demoFolderId === '0'
              ? goToWorkspacePage()
              : goToFolderPage(demoFolderId, workspaceId);
          }}
        />
      )}
    </>
  );
};
